import { useQuery } from '@tanstack/react-query';
import { getYarikanoGirlsPublic } from 'api';
import { HorizontalVirtualList, Loading, RankingBadge, Text } from 'components';
import useImpressionTracking from 'hooks/useImpressionTracking';
import { useCallback } from 'react';
import {
  TEXT_STRING,
  YARIKANO_GIRLS_SCROLL_CONTAINER_ID,
  queryKeys,
} from '../../constants';
import { TYarikanoGirl } from 'types';
import NoAvatar from '../../assets/images/avatar.svg';
import {
  claimTrackingSlot,
  getYarikanoCardClickTrackingKey,
  getYarikanoCardImpressionTrackingKey,
  trackYarikanoCardClick,
  trackYarikanoCardImpression,
} from 'utils';

type ListYarikanoGirlsProps = {
  isActive: boolean;
  isTrackingReady: boolean;
  userId?: number;
};

type YarikanoGirlCardProps = {
  item: TYarikanoGirl;
  rank: number;
  isActive: boolean;
  isTrackingReady: boolean;
  userId?: number;
};

function YarikanoGirlCard({
  item,
  rank,
  isActive,
  isTrackingReady,
  userId,
}: YarikanoGirlCardProps) {
  const trackingKey = getYarikanoCardImpressionTrackingKey(item.id, userId);

  const handleImpression = useCallback(() => {
    trackYarikanoCardImpression({
      yarikanoGirlId: item.id,
      rank,
      userId,
    });
  }, [item.id, rank, userId]);

  const cardRef = useImpressionTracking<HTMLAnchorElement>({
    trackingKey,
    isActive,
    onImpression: handleImpression,
  });

  return (
    <a
      ref={cardRef}
      href={item.profileUrl}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={TEXT_STRING.SEARCH_PAGE.YARIKANO_CARD_LINK_ARIA_LABEL.replace(
        '{{rank}}',
        String(rank),
      )}
      className="flex flex-col cursor-pointer bg-pink-50 border-2 border-solid border-pink-400 rounded-lg flex-shrink-0 overflow-hidden no-underline text-inherit"
      style={{ width: '100%' }}
      onClick={() => {
        if (
          isTrackingReady &&
          claimTrackingSlot(getYarikanoCardClickTrackingKey(item.id, userId))
        ) {
          trackYarikanoCardClick({
            yarikanoGirlId: item.id,
            rank,
            userId,
          });
        }
      }}
    >
      <div className="w-full relative">
        <div
          className={`h-full w-full absolute top-0 left-0 rounded-t-lg border border-neutral-200 
            background-gradient bg-gradient-to-br from-neutral-200 via-white to-neutral-200`}
        />
        <img
          src={item.imageUrl || NoAvatar}
          alt=""
          className="w-full aspect-[0.9] object-cover object-top rounded-t-lg z-[1] relative"
          onError={(e) => (e.currentTarget.src = NoAvatar)}
        />
        <RankingBadge
          rank={rank}
          className="!bottom-0 z-[2]"
          disableNavigate
          maxRank={10}
        />
      </div>
    </a>
  );
}

function ListYarikanoGirls({
  isActive,
  isTrackingReady,
  userId,
}: ListYarikanoGirlsProps) {
  const { data = [], isLoading } = useQuery({
    queryKey: [queryKeys.YARIKANO_GIRLS_PUBLIC],
    queryFn: getYarikanoGirlsPublic,
  });

  const renderItem = useCallback(
    (item: TYarikanoGirl, index: number) => {
      return (
        <YarikanoGirlCard
          item={item}
          rank={index + 1}
          isActive={isActive}
          isTrackingReady={isTrackingReady}
          userId={userId}
        />
      );
    },
    [isActive, isTrackingReady, userId],
  );

  const renderLoading = useCallback(() => {
    return (
      <div className="flex aspect-[0.9] items-center justify-center bg-pink-50 border-2 border-solid border-pink-400 rounded-lg flex-shrink-0 overflow-hidden">
        <Loading />
      </div>
    );
  }, []);

  if (isLoading) {
    return (
      <div className="px-8px pt-4 mt-8">
        <Loading />
      </div>
    );
  }

  if (!data.length) {
    return null;
  }

  return (
    <div className="px-8px pt-2">
      <div className="flex items-center gap-2">
        <Text bold className="text-[12px] 2xs:text-[14px] xs:text-[16px]">
          {TEXT_STRING.SEARCH_PAGE.YARIKANO_GIRLFRIEND}
        </Text>
        <span className="rounded px-1.5 py-1 text-[10px] mt-1 font-bold text-white bg-pink-500 leading-none">
          NEW
        </span>
      </div>
      <HorizontalVirtualList
        items={data}
        hasNext={false}
        isFetchingNext={false}
        onReachEnd={() => undefined}
        renderItem={renderItem}
        renderLoading={renderLoading}
        scrollableTarget={YARIKANO_GIRLS_SCROLL_CONTAINER_ID}
        itemWidth="25%"
        bufferSize={1}
        className="!py-2"
      />
    </div>
  );
}

export default ListYarikanoGirls;
